import AsyncStorage from '@react-native-async-storage/async-storage'
import { QueryClient } from '@tanstack/react-query'
import { BasicIndex, createCollection, createReactProvider, setLogger } from 'pbtsdb'
import { AsyncAuthStore } from 'pocketbase'
import { PocketBaseTS } from 'pocketbase-ts'
import EventSource from 'react-native-sse'
import type { Orgs, Schema, UserOrg, Users } from '@/types/pbSchema'
import { PB_SERVER_ADDR } from './config'
import { log } from './logger'
import { LCASE_CHARS, randomString } from './string'
import type { UserMetadata, UserSession } from './types'

export { eq } from '@tanstack/db'

global.EventSource = EventSource as unknown as typeof global.EventSource

export { PB_SERVER_ADDR }

// Seconds remaining until the current auth token expires, or 0 if missing/expired
export function getTokenTTL(): number {
    const token = pb.authStore.token
    if (!token) return 0
    try {
        const payload = JSON.parse(atob(token.split('.')[1]))
        const remaining = (payload.exp ?? 0) - Date.now() / 1000
        return Math.max(0, remaining)
    } catch {
        return 0
    }
}

let refreshInFlight: Promise<string> | null = null

// Refresh the auth token via the custom endpoint that supports a 7-day grace period.
// Deduplicates concurrent calls so only one network request is made.
export async function refreshAuthToken(): Promise<string> {
    if (refreshInFlight) return refreshInFlight

    refreshInFlight = (async () => {
        try {
            const res = await pb.send('/api/auth/refresh', {
                method: 'POST',
                headers: { Authorization: `Bearer ${pb.authStore.token}` },
            })
            // Preserve the existing auth record. authStore.save(token) with no
            // second arg overwrites the record with undefined, which wipes the
            // user — onChange then sees a null record and logs them out. The
            // refresh only mints a new token; the record is unchanged.
            pb.authStore.save(res.accessToken, pb.authStore.record)
            log.info('Auth token refreshed')
            return res.accessToken as string
        } finally {
            refreshInFlight = null
        }
    })()

    return refreshInFlight
}

const initialAuthPromise = AsyncStorage.getItem('pb_auth')

const store = new AsyncAuthStore({
    save: async serialized => AsyncStorage.setItem('pb_auth', serialized),
    initial: initialAuthPromise,
    clear: async () => await AsyncStorage.removeItem('pb_auth'),
})

// Create a Promise that resolves when initial auth is loaded
// We wait for the initial promise AND then verify the store has processed it
export const authStoreReady = initialAuthPromise.then(async storedAuth => {
    if (storedAuth) {
        // Poll briefly to ensure the store has processed the initial value
        let attempts = 0
        while (!store.token && attempts < 10) {
            await new Promise(resolve => setTimeout(resolve, 10))
            attempts++
        }
    }
})

export const pb = new PocketBaseTS<Schema>(PB_SERVER_ADDR, store)

// Disable PocketBase SDK auto-cancellation. With pbtsdb + TanStack Query managing
// request deduplication, PB's auto-cancel causes silent data loss when multiple
// concurrent queries target the same on-demand collection with different filters
// (e.g. analytics loading current + previous period simultaneously).
pb.autoCancellation(false)

// Proactively refresh the token before any request if it's expired or nearly expired.
// This avoids 401s from hitting the server with a stale token, and covers all code
// paths (queries, mutations, realtime) without needing per-call retry logic.
pb.beforeSend = async (url, options) => {
    // Skip for the refresh endpoint itself to avoid infinite loops
    if (url.includes('/api/auth/refresh')) return { url, options }

    if (pb.authStore.token && getTokenTTL() < 30) {
        try {
            await refreshAuthToken()
        } catch {
            // Refresh failed — let the request proceed and fail naturally
        }
    }
    return { url, options }
}

// Add disconnect handler for debugging subscription issues
pb.realtime.onDisconnect = (activeSubscriptions: string[]) => {
    log.warn('PocketBase realtime disconnected', {
        activeSubscriptions,
        wasConnected: activeSubscriptions.length > 0,
    })
}

// The PB SDK has a bug where a failed submitSubscriptions (404 "Missing or invalid
// client id") after a successful PB_CONNECT causes a full disconnect with no retry.
// The SDK clears clientId before calling connectErrorHandler, which sees
// !clientId && !reconnectAttempts and gives up. This leaves realtime silently dead.
// We detect this by checking isConnected + whether subscriptions still exist,
// and force a reconnect via the private connect() method.
export function reconnectRealtimeIfDead(): void {
    // subscriptions/connect are private in the type defs but exist at runtime
    const rt = pb.realtime as unknown as {
        isConnected: boolean
        subscriptions: Record<string, unknown[]>
        connect: () => Promise<void>
    }
    const hasSubscriptions = Object.keys(rt.subscriptions).length > 0
    if (!rt.isConnected && hasSubscriptions) {
        log.warn('PocketBase realtime found dead with active subscriptions, reconnecting')
        rt.connect()
    }
}

export function usePocketBase() {
    return pb
}

export const newRecordId = () => randomString(15, LCASE_CHARS)

// Configure logger to use our centralized logging (do this once at module load)
setLogger({
    debug: () => {},
    info: (msg, context) => {
        if (context) {
            log.info('[pbtsdb]', msg, context)
        } else {
            log.info('[pbtsdb]', msg)
        }
    },
    warn: (msg, context) => {
        if (context) {
            log.warn('[pbtsdb]', msg, context)
        } else {
            log.warn('[pbtsdb]', msg)
        }
    },
    error: (msg, context) => {
        if (context) {
            log.error('[pbtsdb]', msg, context)
        } else {
            log.error('[pbtsdb]', msg)
        }
    },
})

const queryClient = new QueryClient()

const newCollection = createCollection<Schema>(pb, queryClient)

const indexing = {
    collectionOptions: {
        autoIndex: 'eager' as const,
        defaultIndexType: BasicIndex,
    },
}

const tags = newCollection('tags', {
    omitOnInsert: ['created', 'updated'],
    ...indexing,
})
const orgs = newCollection('orgs', {
    omitOnInsert: ['created', 'updated'],
    ...indexing,
})
const addresses = newCollection('addresses', {
    omitOnInsert: ['updated', 'created'],
    syncMode: 'on-demand', // will only be loaded from it's related records
    ...indexing,
})
const locations = newCollection('locations', {
    omitOnInsert: ['updated', 'created', 'thumbnail', 'inactive', 'tags'],
    ...indexing,
})

const customers = newCollection('customers', {
    omitOnInsert: ['created', 'updated'],
    expand: {
        address: addresses,
    },
    ...indexing,
})

const jobs = newCollection('jobs', {
    omitOnInsert: ['org', 'created', 'updated'],
    expand: {
        address: addresses,
    },
    ...indexing,
})

const users = newCollection('users', {
    omitOnInsert: ['created', 'updated', 'password', 'tokenKey'],
    ...indexing,
})

const time_entries = newCollection('time_entries', {
    syncMode: 'on-demand',
    omitOnInsert: ['created', 'updated', 'clock_in_location', 'clock_out_location'],
    ...indexing,
})

const time_entry_audits = newCollection('time_entry_audits', {
    syncMode: 'on-demand',
    omitOnInsert: ['created'],
    ...indexing,
})

const invitations = newCollection('invitations', {
    omitOnInsert: ['created', 'updated'],
    ...indexing,
})

const movement = newCollection('movement', {
    syncMode: 'on-demand',
    omitOnInsert: [],
    ...indexing,
})

const user_org = newCollection('user_org', {
    omitOnInsert: ['created', 'updated'],
    expand: {
        user: users,
        org: orgs,
    },
    ...indexing,
})

// push_tokens is not yet in the auto-generated Schema type until migration runs
// biome-ignore lint/suspicious/noExplicitAny: collection registered before schema regeneration
const push_tokens = (newCollection as any)('push_tokens', {
    omitOnInsert: ['created', 'updated'],
    ...indexing,
})

// biome-ignore lint/suspicious/noExplicitAny: collection registered before schema regeneration
const location_auth_events = (newCollection as any)('location_auth_events', {
    syncMode: 'on-demand',
    omitOnInsert: ['created'],
    ...indexing,
})

const stores = {
    tags,
    orgs,
    addresses,
    locations,
    jobs,
    customers,
    users,
    time_entries,
    time_entry_audits,
    invitations,
    movement,
    user_org,
    push_tokens,
    location_auth_events,
}

const { Provider: PBTSDBProvider, useStore } = createReactProvider(stores)

export function getUserFromAuthStore(primaryOrgSlug?: string | null): UserSession | null {
    const authRecord = pb.authStore.record as (Users & { metadata?: UserMetadata }) | null
    const authToken = pb.authStore.token

    if (!authRecord || !authToken || !pb.authStore.isValid) {
        return null
    }

    return {
        id: authRecord.id,
        name: authRecord.name,
        email: authRecord.email,
        primaryOrgSlug: primaryOrgSlug ?? undefined,
        geo_tracking_disabled: authRecord.geo_tracking_disabled ?? false,
        metadata: authRecord.metadata,
    }
}

export async function seedUserOrg(userRecord: Users, orgRecord: Orgs, userOrgRecord: UserOrg) {
    await Promise.all([stores.users.preload(), stores.orgs.preload(), stores.user_org.preload()])
    stores.users.utils?.writeUpsert(userRecord)
    stores.orgs.utils?.writeUpsert(orgRecord)
    stores.user_org.utils?.writeUpsert(userOrgRecord)
}

export async function preloadStores() {
    stores.orgs.preload()
    stores.jobs.preload()
    stores.locations.preload()
}

export async function fetchAndSeedUserOrg() {
    await Promise.all([stores.users.preload(), stores.orgs.preload(), stores.user_org.preload()])
    const userOrgs = await pb.collection('user_org').getFullList()
    for (const userOrgRecord of userOrgs) {
        stores.user_org.utils?.writeUpsert(userOrgRecord)
    }
}

export async function clearStores() {
    for (const store of Object.values(stores)) {
        await store.cleanup()
    }
}

export type StoreKey = keyof typeof stores

export { PBTSDBProvider, queryClient, stores, useStore }
